import React, { ReactElement, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Avatar, Button, Stack } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { uploadFile } from '~api/services/utils/upload_utils'
import { images } from '~assets/images'
import { updateUserInfo } from '~redux/slices/authSlice'
import { AssetTypeEnum, User } from '~types'

type ProfileEditWidgetProps = {
  user: User
  size: number
}

const ProfileEditWidget = ({ user, size }: ProfileEditWidgetProps): ReactElement => {
  const dispatch = useDispatch()
  const [isUploading, setIsUploading] = useState(false)
  const [profileImage, setProfileImage] = useState<string | null>(user.image)

  const handleProfileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files && e.target.files[0]) {
      setIsUploading(true)
      try {
        const file = e.target.files[0]
        const uploadResponse = await uploadFile(
          user.id,
          user.identity_id,
          file,
          AssetTypeEnum.enum.Logo,
          null,
          null,
          false,
        )

        if (uploadResponse && uploadResponse.url) {
          const uploadedUrl = uploadResponse.url
          const editResponse = await apiZodios.core_editContactImage({
            user_id: user.id,
            image: uploadedUrl,
          })

          if (editResponse.image) {
            setProfileImage(editResponse.image)
            dispatch(updateUserInfo({ ...user, image: editResponse.image }))
          }
        }
      } catch (error) {
        console.error('Error uploading profile image:', error)
      } finally {
        setIsUploading(false)
      }
    }
  }

  return (
    <Stack spacing={1} alignItems='center'>
      <Avatar
        src={isUploading ? images.loadingSpinner : (profileImage ?? undefined)}
        sx={{ width: size, height: size }}
      >
        {!profileImage && !isUploading ? user.name?.charAt(0) : undefined}
      </Avatar>
      <Button variant='outlined' color='secondary' component='label' htmlFor='profile-upload'>
        <input
          type='file'
          onChange={handleProfileChange}
          id='profile-upload'
          style={{ display: 'none' }}
          accept='image/*'
        />
        {profileImage ? 'Change profile picture' : 'Upload profile picture'}
      </Button>
    </Stack>
  )
}

export default ProfileEditWidget
